import { FramePacer, type FrameLimit, type FramePlan } from "odyssey-shared";
import { localStore, type SettingsStore } from "./settings.ts";
import { writeFrameLimit } from "./display-settings.ts";

/**
 * The page's frame limit (ADR-093 decisions 40 to 45): the viewer's choice put in force on Phaser's loop, and the
 * display's rate read from the frames the loop gets, which Settings reads to offer 120. The pacer
 * (`odyssey-shared`) decides which animation frames are drawn; this puts it between the loop and the game's step, so
 * a skipped frame runs nothing of the step, and the next drawn frame is given the time of the frames it covers. The
 * game's own clocks (the input tick, the interpolation) count in the time a step is given, so they keep their pace
 * whatever is drawn.
 */

/**
 * The part of a Phaser game the limiter reaches: its step, whether its loop has started, and the loop's callback.
 */
export interface SteppedGame {
  step: (time: number, delta: number) => void;
  isRunning: boolean;
  readonly loop: { callback: (time: number, delta: number) => void };
}

/**
 * The page's frame limit: the choice in force, the display's rate as measured, and whoever follows them.
 */
export class FrameLimiter {
  private readonly pacer: FramePacer;
  private readonly listeners = new Set<() => void>();
  private readonly store: SettingsStore | null;

  /**
   * @param limit The choice in force.
   * @param store Where a new choice is kept; this browser's `localStorage` when omitted, nothing when null (the
   *   benchmark's page, which keeps and shows no choice).
   */
  constructor(limit: FrameLimit, store?: SettingsStore | null) {
    this.pacer = new FramePacer(limit);
    this.store = store === undefined ? localStore() : store;
    this.pacer.onRefresh = (): void => this.tell();
  }

  /**
   * The choice in force.
   *
   * @returns The choice.
   */
  get limit(): FrameLimit {
    return this.pacer.limit;
  }

  /**
   * The display's rate as measured, or null before it is first taken.
   *
   * @returns The rate in Hz.
   */
  get refresh(): number | null {
    return this.pacer.refresh;
  }

  /**
   * How the choice is drawn on the display as measured.
   *
   * @returns The plan.
   */
  get plan(): FramePlan {
    return this.pacer.plan;
  }

  /**
   * Takes the viewer's new choice, keeps it, and puts it in force from the next frame.
   *
   * @param limit The choice.
   */
  setLimit(limit: FrameLimit): void {
    this.pacer.setLimit(limit);
    writeFrameLimit(limit, this.store);
    this.tell();
  }

  /**
   * Calls back whenever the choice or the display's rate changes.
   *
   * @param listener Called after the change; it reads the new values from this object.
   * @returns A function that stops the calls.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Takes one animation frame, for a page whose loop is not Phaser's (the gallery's, which only measures the
   * display's rate).
   *
   * @param time The frame's time in ms.
   * @returns True when the frame is to be drawn.
   */
  frame(time: number): boolean {
    return this.pacer.tick(time);
  }

  /**
   * Puts the limiter between a game's loop and its step. A game that has not started has its step replaced, which
   * the loop takes when it starts; one that has started has its loop's callback replaced. A frame the pacer skips
   * adds its time to what the next drawn frame is given.
   *
   * @param game The game.
   */
  install(game: SteppedGame): void {
    let owed = 0;
    const limit = (inner: (time: number, delta: number) => void): ((time: number, delta: number) => void) => {
      return (time, delta) => {
        owed += delta;
        if (!this.frame(time)) {
          return;
        }
        const given = owed;
        owed = 0;
        inner(time, given);
      };
    };
    if (game.isRunning) {
      game.loop.callback = limit(game.loop.callback);
      return;
    }
    const step = game.step;
    game.step = limit((time, delta) => step.call(game, time, delta));
  }

  /**
   * Tells every follower.
   */
  private tell(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}
